import { Link } from "react-router-dom";
import {
  ArrowRight,
  Mail,
  MessageCircle,
  Globe2,
  Heart,
  Briefcase,
} from "lucide-react";
import { PRODUCTS, COMPANY } from "@/data/products";
import HeroCarousel from "@/components/site/HeroCarousel";
import CommodityPrices from "@/components/site/CommodityPrices";
import GlobalReach from "@/components/site/GlobalReach";

const WHO_IMG =
  "https://images.unsplash.com/photo-1753771145085-d71e89c23092";
const CLIMATE_IMG =
  "https://images.pexels.com/photos/11666903/pexels-photo-11666903.jpeg";
const CODE_IMG =
  "https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg";
const CAREERS_IMG =
  "https://images.unsplash.com/photo-1664565262236-65166affe34a";
const COMMODITIES_IMG =
  "https://images.unsplash.com/photo-1753771145110-b3ddcd1b0ae2";

// Latest news (Glencore-style dated list)
const NEWS = [
  ["12/06/2026", "Multi-year copper cathode offtake signed with European refiner"],
  ["18/05/2026", "Q2 2026 production report: copper output up 12%"],
];

// Latest insights — Glencore "Insights & Stories" 3-card grid
const INSIGHTS = [
  {
    date: "06/11/2026",
    title: "Making mining history: Team EELLEVEN delivers at depth",
    image: COMMODITIES_IMG,
  },
  {
    date: "17/10/2026",
    title:
      "Britannia-grade refining: how Zambia's copper meets the LME standard",
    image: WHO_IMG,
  },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO CAROUSEL — Glencore-style auto-rotating banner */}
      <HeroCarousel />

      {/* MINE-FOR-SALE ANNOUNCEMENT BAR — cinematic, high-conversion */}
      <section
        className="relative border-b border-[var(--eel-border)] overflow-hidden"
        data-testid="home-mfs-strip"
      >
        <img
          src="https://images.pexels.com/photos/1078884/pexels-photo-1078884.jpeg?auto=compress&cs=tinysrgb&w=2400"
          alt="Copperbelt mine asset"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--eel-bg)] via-[var(--eel-bg)]/85 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rounded-full copper-bg animate-pulse" />
              <p className="label-eyebrow text-[var(--eel-copper)]">
                Confidential Opportunity · Now Open
              </p>
            </div>
            <h2 className="mt-5 font-editorial text-3xl md:text-4xl lg:text-5xl leading-[1.05] text-white">
              Copperbelt mining asset —
              <span className="font-editorial-italic copper-text">
                {" "}
                for sale or joint venture.
              </span>
            </h2>
            <p className="mt-5 max-w-2xl text-zinc-300 leading-relaxed font-light">
              Large-Scale Exploration Licence in Lufwanyama & Luanshya, Zambia.
              Confirmed on-site minerals: copper, silica, quartz and emerald.
              Open to international mining houses, funds and strategic
              partners.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/mine-for-sale"
                data-testid="home-mfs-cta"
                className="px-6 py-3.5 copper-bg text-black font-semibold uppercase tracking-widest text-xs hover:bg-[var(--eel-copper-hover)] transition-colors inline-flex items-center gap-3"
              >
                View the Asset <ArrowRight size={14} />
              </Link>
              <Link
                to="/mine-for-sale#enquire"
                data-testid="home-mfs-cta-dr"
                className="px-6 py-3.5 border border-white/40 text-white font-semibold uppercase tracking-widest text-xs hover:bg-white hover:text-black transition-colors inline-flex items-center gap-3"
              >
                Request Data Room
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
            {[
              ["Region", "Copperbelt · Zambia"],
              ["Licence", "Large-Scale Exp."],
              ["Location", "Lufwanyama · Luanshya"],
              ["Minerals", "Cu · SiO₂ · Quartz · Emerald"],
            ].map(([k, v], i) => (
              <div
                key={i}
                data-testid={`home-mfs-fact-${i}`}
                className="bg-[var(--eel-bg)]/70 backdrop-blur-sm p-5"
              >
                <p className="label-eyebrow text-zinc-500">{k}</p>
                <p className="mt-2 font-editorial text-sm md:text-base text-white leading-tight">
                  {v}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR PURPOSE — big editorial banner */}
      <section
        className="section-paper border-y border-[var(--eel-paper-2)]"
        data-testid="home-purpose"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <p className="label-eyebrow">Our purpose</p>
          <h2 className="font-editorial text-4xl md:text-5xl lg:text-6xl mt-5 leading-[1.04] max-w-4xl text-[var(--eel-ink)]">
            Responsibly sourcing the commodities that
            <span className="font-editorial-italic copper-text">
              {" "}
              advance everyday life.
            </span>
          </h2>
        </div>
      </section>

      {/* WHO WE ARE — image + paragraph + CTA (Glencore signature block) */}
      <section
        className="border-b border-[var(--eel-border)]"
        data-testid="home-who-we-are"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-6">
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={WHO_IMG}
                alt="Operations"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6 flex flex-col justify-center">
            <p className="label-eyebrow">Who we are</p>
            <h2 className="font-editorial mt-5 text-4xl md:text-5xl lg:text-6xl leading-[1.04]">
              A Zambian-grown
              <span className="font-editorial-italic copper-text">
                {" "}
                natural resource business.
              </span>
            </h2>
            <p className="mt-7 text-base md:text-lg text-zinc-300 leading-relaxed font-light">
              EELLEVEN MINING is a privately-held operator and merchant of base
              metals and battery feedstock. Through our scale and
              diversified product range, we responsibly supply the commodities
              that are fundamental to the building blocks of modern life.
            </p>
            <Link
              to="/about"
              data-testid="who-we-are-cta"
              className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-widest copper-text link-underline"
            >
              Find out more <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* LATEST NEWS — long dated list (Glencore's primary content block) */}
      <section
        className="section-paper border-b border-[var(--eel-paper-2)]"
        data-testid="home-news"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="flex items-end justify-between mb-12 pb-8 border-b border-[var(--eel-paper-2)]">
            <h2 className="font-editorial text-4xl md:text-5xl text-[var(--eel-ink)]">
              Latest news
            </h2>
            <Link
              to="/about"
              data-testid="news-all"
              className="text-xs uppercase tracking-widest copper-text link-underline inline-flex items-center gap-2"
            >
              All news <ArrowRight size={12} />
            </Link>
          </div>
          <ul className="divide-y divide-[var(--eel-paper-2)]">
            {NEWS.map(([date, title], i) => (
              <li
                key={i}
                data-testid={`news-${i}`}
                className="py-5 grid grid-cols-1 md:grid-cols-12 gap-4 group cursor-pointer"
              >
                <span className="md:col-span-2 font-heading text-sm text-[var(--eel-ink-muted)]">
                  {date}
                </span>
                <span className="md:col-span-9 font-editorial text-xl md:text-2xl text-[var(--eel-ink)] leading-tight group-hover:copper-text transition-colors">
                  {title}
                </span>
                <span className="md:col-span-1 md:text-right text-xs uppercase tracking-widest copper-text opacity-0 group-hover:opacity-100 transition-opacity">
                  Read →
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* OUR COMMODITIES — paragraph + product grid (Glencore "We have the raw materials") */}
      <section
        className="border-b border-[var(--eel-border)]"
        data-testid="home-commodities"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <p className="label-eyebrow">Our commodities</p>
            <h2 className="font-editorial mt-5 text-4xl md:text-5xl lg:text-6xl leading-[1.04]">
              We have the raw materials
              <span className="font-editorial-italic copper-text">
                {" "}
                the world needs.
              </span>
            </h2>
            <p className="mt-7 text-base md:text-lg text-zinc-300 leading-relaxed font-light">
              We source, process, ship and market the transition metals and
              minerals essential to electrification and to
              everyday industry — copper, cobalt, manganese and coal.
            </p>
            <Link
              to="/products"
              data-testid="commodities-cta"
              className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-widest copper-text link-underline"
            >
              Find out more about our business <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-4">
            {PRODUCTS.slice(0, 9).map((p) => (
              <Link
                key={p.slug}
                to={`/products#${p.slug}`}
                data-testid={`home-product-card-${p.slug}`}
                className="group block"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <p className="mt-3 font-editorial text-base text-white group-hover:copper-text transition-colors leading-tight">
                  {p.name}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CLIMATE / SUSTAINABILITY — full image + text */}
      <section
        className="relative h-[70vh] min-h-[520px] overflow-hidden border-b border-[var(--eel-border)]"
        data-testid="home-climate"
      >
        <img
          src={CLIMATE_IMG}
          alt="Operations"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--eel-bg)] via-[var(--eel-bg)]/75 to-[var(--eel-bg)]/35" />
        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-10 flex items-center">
          <div className="max-w-xl">
            <p className="label-eyebrow text-[var(--eel-copper)]">
              Our position on climate change
            </p>
            <h2 className="font-editorial mt-5 text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-white">
              Powering the
              <span className="font-editorial-italic copper-text">
                {" "}
                transition.
              </span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-zinc-200 leading-relaxed font-light">
              The commodities in our portfolio support society&apos;s growing
              energy and infrastructure needs as well as global efforts to
              transition to a low-carbon economy.
            </p>
            <Link
              to="/about"
              data-testid="climate-cta"
              className="mt-7 inline-flex items-center gap-2 text-sm uppercase tracking-widest copper-text link-underline"
            >
              Download the reports <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* COMMODITY PRICES — Glencore share-price equivalent */}
      <CommodityPrices />

      {/* CORPORATE CALENDAR + PUBLICATIONS — 2-column block */}
      <section
        className="border-b border-[var(--eel-border)]"
        data-testid="home-corp"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--eel-border)] border border-[var(--eel-border)]">
          <Link
            to="/invest"
            data-testid="home-corp-investors"
            className="bg-[var(--eel-surface)] p-10 hover:bg-[var(--eel-surface-2)] transition-colors group"
          >
            <p className="label-eyebrow text-zinc-500">For investors</p>
            <h3 className="font-editorial text-3xl md:text-4xl mt-3 leading-tight">
              Corporate calendar &
              <span className="font-editorial-italic copper-text">
                {" "}
                reports
              </span>
            </h3>
            <p className="mt-3 text-sm text-zinc-400 font-light">
              Investor briefings, KPI updates and audit-ready documentation.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest copper-text link-underline">
              Find out more <ArrowRight size={12} />
            </span>
          </Link>
          <Link
            to="/about"
            data-testid="home-corp-publications"
            className="bg-[var(--eel-surface)] p-10 hover:bg-[var(--eel-surface-2)] transition-colors group"
          >
            <p className="label-eyebrow text-zinc-500">Publications</p>
            <h3 className="font-editorial text-3xl md:text-4xl mt-3 leading-tight">
              Reports &
              <span className="font-editorial-italic copper-text">
                {" "}
                disclosures
              </span>
            </h3>
            <p className="mt-3 text-sm text-zinc-400 font-light">
              Annual report, sustainability report, ethics & compliance report.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest copper-text link-underline">
              Download latest publications <ArrowRight size={12} />
            </span>
          </Link>
        </div>
      </section>

      {/* OUR CULTURE — Code of Conduct + Careers 2-card block */}
      <section
        className="border-b border-[var(--eel-border)]"
        data-testid="home-culture"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <p className="label-eyebrow">Our culture</p>
          <h2 className="font-editorial mt-5 text-4xl md:text-5xl lg:text-6xl leading-[1.04]">
            Fairness, respect,
            <span className="font-editorial-italic copper-text">
              {" "}
              opportunity.
            </span>
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/about"
              data-testid="culture-card-code"
              className="group relative block overflow-hidden aspect-[3/2]"
            >
              <img
                src={CODE_IMG}
                alt="Code of Conduct"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute inset-0 p-8 flex flex-col justify-end">
                <Heart size={22} className="copper-text" />
                <h3 className="mt-4 font-editorial text-3xl md:text-4xl text-white">
                  Our Code of Conduct
                </h3>
                <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest copper-text link-underline">
                  Find out more <ArrowRight size={12} />
                </span>
              </div>
            </Link>
            <Link
              to="/contact"
              data-testid="culture-card-careers"
              className="group relative block overflow-hidden aspect-[3/2]"
            >
              <img
                src={CAREERS_IMG}
                alt="Careers"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute inset-0 p-8 flex flex-col justify-end">
                <Briefcase size={22} className="copper-text" />
                <h3 className="mt-4 font-editorial text-3xl md:text-4xl text-white">
                  Advancing careers
                </h3>
                <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest copper-text link-underline">
                  Read more <ArrowRight size={12} />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* SITES (kept) */}
      <section className="border-b border-[var(--eel-border)]" data-testid="home-sites">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <p className="label-eyebrow">Location</p>
          <h2 className="font-editorial mt-5 text-4xl md:text-5xl lg:text-6xl leading-[1.04]">
            Our yard in
            <span className="font-editorial-italic copper-text"> Zambia.</span>
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {COMPANY.sites.map((s, i) => (
              <div
                key={s.name}
                data-testid={`home-site-${i}`}
                className="border border-[var(--eel-border)] p-7"
              >
                <p className="label-eyebrow text-zinc-500">
                  {String(i + 1).padStart(2, "0")} · {s.label}
                </p>
                <h3 className="mt-4 font-editorial text-2xl text-white">
                  {s.name}
                </h3>
                <p className="mt-3 text-sm text-zinc-400 font-light">
                  {s.address}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL REACH (kept) */}
      <GlobalReach />

      {/* LATEST INSIGHTS — Glencore 3-card photo grid */}
      <section
        className="section-paper border-y border-[var(--eel-paper-2)]"
        data-testid="home-insights"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="flex items-end justify-between mb-12">
            <h2 className="font-editorial text-4xl md:text-5xl text-[var(--eel-ink)]">
              Latest insights and
              <span className="font-editorial-italic copper-text"> stories</span>
            </h2>
            <Link
              to="/about"
              data-testid="insights-all"
              className="text-xs uppercase tracking-widest copper-text link-underline inline-flex items-center gap-2"
            >
              See all <ArrowRight size={12} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {INSIGHTS.map((s, i) => (
              <article
                key={i}
                data-testid={`insight-${i}`}
                className="group cursor-pointer"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[var(--eel-paper-2)]">
                  <img
                    src={s.image}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <p className="mt-5 font-heading text-xs text-[var(--eel-ink-muted)]">
                  {s.date}
                </p>
                <h3 className="mt-2 font-editorial text-xl md:text-2xl text-[var(--eel-ink)] leading-tight group-hover:copper-text transition-colors">
                  {s.title}
                </h3>
                <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest copper-text">
                  Read more <ArrowRight size={12} />
                </span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT INFO STRIP (kept) */}
      <section className="border-t border-[var(--eel-border)] bg-[var(--eel-surface)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--eel-border)] border border-[var(--eel-border)]">
          <a
            href={`mailto:${COMPANY.email}`}
            data-testid="home-contact-email"
            className="bg-[var(--eel-bg)] p-7 hover:bg-[var(--eel-surface-2)] transition-colors"
          >
            <Mail className="copper-text" size={20} />
            <p className="label-eyebrow text-zinc-500 mt-3">Trade Desk</p>
            <p className="mt-2 font-editorial text-xl break-all">
              {COMPANY.email}
            </p>
          </a>
          <a
            href={`https://wa.me/${COMPANY.whatsappPrimaryDigits}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-contact-wa-ireland"
            className="bg-[var(--eel-bg)] p-7 hover:bg-[var(--eel-surface-2)] transition-colors"
          >
            <MessageCircle className="text-green-400" size={20} />
            <p className="label-eyebrow text-zinc-500 mt-3">WhatsApp · Ireland</p>
            <p className="mt-2 font-editorial text-xl">
              {COMPANY.whatsappPrimary}
            </p>
          </a>
          <a
            href={`tel:${COMPANY.phoneZambia}`}
            data-testid="home-contact-phone-zm"
            className="bg-[var(--eel-bg)] p-7 hover:bg-[var(--eel-surface-2)] transition-colors"
          >
            <Globe2 className="copper-text" size={20} />
            <p className="label-eyebrow text-zinc-500 mt-3">Phone · Zambia</p>
            <p className="mt-2 font-editorial text-xl">{COMPANY.phoneZambia}</p>
          </a>
        </div>
      </section>
    </div>
  );
}
